import { RouterProvider } from '@tanstack/react-router';
import { Component, type ErrorInfo, type ReactNode, useEffect } from 'react';
import { entry, router } from './routes';

declare global {
  var __onautoRouteInventory: {
    routes: Array<{
      pattern: string;
      path: string;
      title: string;
      page: string;
      standalone: boolean;
      isPublic: boolean;
    }>;
    entry: typeof entry;
  };
}

class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(_error: Error, _info: ErrorInfo) {}
  render() {
    return this.state.failed ? (
      <main className="grid min-h-screen place-items-center bg-background p-6 text-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">Seattle Bus Times</p>
          <h1 className="mt-2 text-3xl font-semibold">We could not open the departure board.</h1>
          <p className="mt-3 text-muted-foreground">Please reload and try again.</p>
        </div>
      </main>
    ) : (
      this.props.children
    );
  }
}

function AppRuntime() {
  useEffect(() => {
    document.getElementById('root')?.setAttribute('data-app-ready', '1');
    const routes = Object.values(router.routesById)
      .filter((route) => route.id !== '__root__')
      .map((route) => {
        const data = route.options.staticData as {
          title: string;
          page: string;
          standalone: boolean;
          isPublic: boolean;
        };
        return {
          pattern: route.fullPath.replace(/\$([^/]+)/g, '[$1]'),
          path: route.fullPath,
          title: data.title,
          page: data.page,
          standalone: data.standalone,
          isPublic: data.isPublic,
        };
      });
    globalThis.__onautoRouteInventory = { routes, entry };
  }, []);
  return <RouterProvider router={router} />;
}

export function App() {
  return (
    <AppErrorBoundary>
      <AppRuntime />
    </AppErrorBoundary>
  );
}
